import * as Cmp from "portal/utils/comparators";
import {
  Almanac,
  Category,
  CategoryChange,
  Role,
  roleFromJSON,
  roleToJSON,
  SavedCategory,
  SavedPlantProfile,
} from "protos/robot_syncer/plant_profile";
import {
  ALMANAC_CATEGORY_DEFAULT,
  WEED_CATEGORY_BROADLEAF,
  WEED_CATEGORY_GRASS,
  WEED_CATEGORY_OFFSHOOT,
  WEED_CATEGORY_PURSLANE,
} from "portal/utils/almanac";
import {
  AlmanacConfig,
  AlmanacTypeCategory,
  Formula,
} from "protos/almanac/almanac";
import {
  almanacWithoutFineTuneFields,
  isValidAlmanac,
} from "portal/utils/shootTime";
import { CHIP_COLORS } from "portal/theme/theme";
import { i18n as I18n, TFunction } from "i18next";
import { isEqual } from "../equality";
import { RadioChipOption } from "portal/components/RadioChips";
import { titleCase } from "../strings";
import { v4 as uuid } from "uuid";

const CROP_NAME = "Crop";
const BROADLEAF_NAME = "Broadleaf";
const GRASS_NAME = "Grass";
const PURSLANE_NAME = "Purslane";
const OFFSHOOT_NAME = "Offshoot";
const DISREGARD_NAME = "Disregard";

type FixedCategory = {
  name: string;
  role: Role;
  i18nKey: string;
  /** Carbon Default almanac category this name seeds from. */
  almanacCategoryId: string;
};

const FIXED_CATEGORIES = [
  {
    name: BROADLEAF_NAME,
    role: Role.WEED,
    i18nKey: "models.weeds.categories.broadleaf",
    almanacCategoryId: WEED_CATEGORY_BROADLEAF,
  },
  {
    name: GRASS_NAME,
    role: Role.WEED,
    i18nKey: "models.weeds.categories.grass",
    almanacCategoryId: WEED_CATEGORY_GRASS,
  },
  {
    name: PURSLANE_NAME,
    role: Role.WEED,
    i18nKey: "models.weeds.categories.purslane",
    almanacCategoryId: WEED_CATEGORY_PURSLANE,
  },
  {
    name: OFFSHOOT_NAME,
    role: Role.WEED,
    i18nKey: "models.weeds.categories.offshoot",
    almanacCategoryId: WEED_CATEGORY_OFFSHOOT,
  },
  {
    name: CROP_NAME,
    role: Role.CROP,
    i18nKey: "models.crops.crop_one",
    almanacCategoryId: ALMANAC_CATEGORY_DEFAULT,
  },
  {
    name: DISREGARD_NAME,
    role: Role.DISREGARD,
    i18nKey: "models.categoryCollectionProfiles.fields.categories.disregard",
    almanacCategoryId: WEED_CATEGORY_BROADLEAF,
  },
] as const satisfies readonly FixedCategory[];

const FIXED_CATEGORY_NAMES = new Set(
  FIXED_CATEGORIES.map((category) => category.name)
);

export const FIXED_CATEGORY_DEFAULT_ROLES: Readonly<Record<string, Role>> =
  Object.fromEntries(
    FIXED_CATEGORIES.map((category) => [category.name, category.role])
  );

/** Ordered fixed-category choices shown in the "most similar to" field. */
export const MOST_SIMILAR_TO_OPTIONS = [
  BROADLEAF_NAME,
  GRASS_NAME,
  PURSLANE_NAME,
  OFFSHOOT_NAME,
  CROP_NAME,
] as const;

export type MostSimilarToOption = (typeof MOST_SIMILAR_TO_OPTIONS)[number];

const isMostSimilarToOption = (name: string): name is MostSimilarToOption =>
  (MOST_SIMILAR_TO_OPTIONS as readonly string[]).includes(name);

/** Ordered selectable plant-profile roles (excludes UNSPECIFIED / UNRECOGNIZED). */
export const PLANT_PROFILE_ROLES = [
  Role.CROP,
  Role.WEED,
  Role.BENEFICIAL,
  Role.DISREGARD,
] as const;

export const hasVisibleShootTime = (role: Role): boolean =>
  role !== Role.DISREGARD && role !== Role.BENEFICIAL;

/** Roles that every plant profile should include at least one category for. */
export const REQUIRED_PLANT_PROFILE_ROLES = [Role.CROP, Role.WEED] as const;

export type PlantProfileRoleOption = Exclude<
  ReturnType<typeof roleToJSON>,
  "ROLE_UNSPECIFIED" | "UNRECOGNIZED"
>;

export const PLANT_PROFILE_ROLE_OPTIONS: readonly PlantProfileRoleOption[] =
  PLANT_PROFILE_ROLES.map((role) => roleToJSON(role) as PlantProfileRoleOption);

const PLANT_PROFILE_ROLE_I18N_KEYS: Record<PlantProfileRoleOption, string> = {
  [roleToJSON(Role.CROP)]:
    "components.categoryCollectionProfile.roles.crop_one",
  [roleToJSON(Role.WEED)]:
    "components.categoryCollectionProfile.roles.weed_one",
  [roleToJSON(Role.BENEFICIAL)]:
    "components.categoryCollectionProfile.roles.beneficial_one",
  [roleToJSON(Role.DISREGARD)]:
    "components.categoryCollectionProfile.roles.disregard_one",
};

/** Canonical fixed category used when seeding / syncing "most similar to" from role. */
const DEFAULT_MOST_SIMILAR_FOR_ROLE: Record<
  PlantProfileRoleOption,
  MostSimilarToOption
> = {
  [roleToJSON(Role.CROP)]: CROP_NAME,
  [roleToJSON(Role.WEED)]: BROADLEAF_NAME,
  // Never shot, but keep real shoot times in case the role changes.
  [roleToJSON(Role.BENEFICIAL)]: BROADLEAF_NAME,
  [roleToJSON(Role.DISREGARD)]: BROADLEAF_NAME,
};

// Record lookups are `T | undefined` under noUncheckedIndexedAccess; these maps
// are exhaustive for PlantProfileRoleOption so the accessors return plain T.
export const plantProfileRoleI18nKey = (role: PlantProfileRoleOption): string =>
  PLANT_PROFILE_ROLE_I18N_KEYS[role]!;

export const defaultMostSimilarForRole = (
  role: PlantProfileRoleOption
): MostSimilarToOption => DEFAULT_MOST_SIMILAR_FOR_ROLE[role]!;

/** Initial "most similar to" for a category: its name if offered, else its role's default (e.g. Disregard). */
export const defaultMostSimilarTo = (
  name: string,
  role: PlantProfileRoleOption
): MostSimilarToOption =>
  isMostSimilarToOption(name) ? name : defaultMostSimilarForRole(role);

export const DEFAULT_CREATE_CATEGORY_ROLE: PlantProfileRoleOption = roleToJSON(
  Role.WEED
);

export const DISREGARD_ROLE: PlantProfileRoleOption = roleToJSON(
  Role.DISREGARD
);

/**
 * Resolves a category's membership role: stored role, then fixed-name default, then the default
 */
export const resolveCategoryRole = (
  categoryId: string,
  categoryName: string | undefined,
  categoryRoles: Record<string, Role>
): Role =>
  categoryRoles[categoryId] ??
  (categoryName !== undefined
    ? FIXED_CATEGORY_DEFAULT_ROLES[categoryName]
    : undefined) ??
  roleFromJSON(DEFAULT_CREATE_CATEGORY_ROLE);

/**
 * Builds membership roles for categories. Prefers an existing role for the
 * category id; otherwise uses the fixed-category default for the English name.
 */
export const categoryRolesForCategories = (
  categories: { id: string; name: string }[],
  existingRoles?: Record<string, Role>
): Record<string, Role> => {
  const roles: Record<string, Role> = {};
  for (const category of categories) {
    const existing = existingRoles?.[category.id];
    if (existing) {
      roles[category.id] = existing;
      continue;
    }
    const defaultRole = FIXED_CATEGORY_DEFAULT_ROLES[category.name];
    if (defaultRole !== undefined) {
      roles[category.id] = defaultRole;
    }
  }
  return roles;
};

/** Crop / Weed / Beneficial / Disregard; unspecified maps to Weed. */
const roleForSection = (
  categoryId: string,
  categoryName: string | undefined,
  categoryRoles: Record<string, Role>
): Role => {
  const role = resolveCategoryRole(categoryId, categoryName, categoryRoles);
  return new Set<Role>(PLANT_PROFILE_ROLES).has(role) ? role : Role.WEED;
};

/** Groups items into role section buckets in PLANT_PROFILE_ROLES order. (Crop, then Weed, then Beneficial, then Disregard) */
export const groupCategoryOptionsByRole = <T extends { id: string }>(
  options: T[],
  categories: { id: string; name: string }[],
  categoryRoles: Record<string, Role>
): Map<Role, T[]> => {
  const byRole = new Map<Role, T[]>();
  for (const role of PLANT_PROFILE_ROLES) {
    byRole.set(role, []);
  }
  for (const option of options) {
    const category = categories.find(({ id }) => id === option.id);
    byRole
      .get(roleForSection(option.id, category?.name, categoryRoles))
      ?.push(option);
  }
  return byRole;
};

export const orderCategoriesByRole = <T extends { id: string; name: string }>(
  categories: T[],
  categoryRoles: Record<string, Role>
): T[] => {
  const byRole = groupCategoryOptionsByRole(
    categories,
    categories,
    categoryRoles
  );
  return PLANT_PROFILE_ROLES.flatMap((role) => byRole.get(role) ?? []);
};

/**
 * True when the profile has exactly the six English fixed categories with the
 * expected membership roles (missing role treated as the fixed default).
 */
export const isFixedPlantProfile = (profile: {
  categories: { id: string; name: string }[];
  categoryRoles: Record<string, Role>;
}): boolean => {
  if (profile.categories.length !== FIXED_CATEGORY_NAMES.size) {
    return false;
  }
  const names = new Set(profile.categories.map((c) => c.name));
  if (names.size !== FIXED_CATEGORY_NAMES.size) {
    return false;
  }
  for (const fixedName of FIXED_CATEGORY_NAMES) {
    if (!names.has(fixedName)) {
      return false;
    }
  }
  for (const category of profile.categories) {
    const expected = FIXED_CATEGORY_DEFAULT_ROLES[category.name];
    if (expected === undefined) {
      return false;
    }
    const actual = profile.categoryRoles[category.id] ?? expected;
    if (actual !== expected) {
      return false;
    }
  }
  return true;
};

export type AlmanacUpdate = {
  formulas?: Formula[];
  sizeBoundariesMm?: number[];
  originCategoryId?: string;
};

/** Merge a partial almanac edit onto the current draft, leaving omitted fields in place. */
export const applyAlmanacUpdate = (
  current: Almanac,
  update: AlmanacUpdate
): Almanac => ({
  ...current,
  sizeBoundariesMm: update.sizeBoundariesMm ?? current.sizeBoundariesMm,
  formulas: update.formulas ?? current.formulas,
  originCategoryId: update.originCategoryId ?? current.originCategoryId,
});

/** Seed a plant-profile Almanac from a legacy AlmanacConfig category. */
export const almanacFromTypeCategory = (
  almanacCategory: AlmanacTypeCategory
): Almanac =>
  Almanac.fromPartial({
    sizeBoundariesMm: [...almanacCategory.sizes],
    formulas: structuredClone(almanacCategory.formulas),
    originCategoryId: almanacCategory.type?.category,
  });

export const getDefaultCategoryNames = (
  t: TFunction,
  i18n: boolean = true
): string[] =>
  FIXED_CATEGORIES.map((category) =>
    i18n ? titleCase(t(category.i18nKey)) : category.name
  );

// sorting fixed categories such that Crop comes first. This will become irrelevant when custom categories are supported - but is useful for user training at the moment
export const sortCategoryOptions = (
  i18n: I18n,
  fixedFirst: string = CROP_NAME
): Cmp.Comparator<{ name: string } | undefined> =>
  Cmp.nullsLast(
    Cmp.first([
      // Put anything with a name of `fixedFirst` first...
      Cmp.comparing(({ name }) =>
        fixedFirst && name.toLowerCase() === fixedFirst.toLowerCase() ? 1 : 2
      ),
      // ...otherwise sort by name.
      Cmp.comparing(
        ({ name }) => name,
        (a, b) => a.localeCompare(b, i18n.language)
      ),
    ])
  );

// helps enforce that specifically named categories exist
export const hasStrictCategories = (
  values: { name: string }[],
  requiredCategories: string[]
): boolean => {
  const actual = new Set(values.map((v) => v.name));
  return requiredCategories.every((name) => actual.has(name));
};

/**
 * Checks equality between two categories. Disregards array ordering for both categories and chip ids.
 * `customerId` / `carbon` are assigned by the backend.
 */
export const areCategoriesEqual = (a?: Category, b?: Category): boolean => {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.id === b.id &&
    a.name.trim() === b.name.trim() &&
    isEqual(a.chipIds, b.chipIds, { orderedArrays: false }) &&
    isEqual(a.almanac, b.almanac, { orderedArrays: false })
  );
};

export type CategorizationMap = Record<string, string>; // point id to category id

export interface CategoryOption extends Category {
  versionId?: string;
  displayName: string;
  collection?: {
    id: string;
    name: string;
    isCarbon?: boolean;
  };
}

export const categoryOptionsFromPlantProfiles = (
  t: TFunction,
  i18n: I18n,
  availableProfiles: SavedPlantProfile[]
): CategoryOption[] => {
  const options: CategoryOption[] = [];

  for (const { profile: plantProfile } of availableProfiles) {
    if (!plantProfile) {
      continue;
    }

    const categoryNames: { name: string }[] = [];
    for (const { category, metadata } of plantProfile.categories) {
      if (category && metadata) {
        categoryNames.push({ name: category.name });
      }
    }
    const translate = translateCategoryNames(t, categoryNames);

    for (const { category, metadata } of plantProfile.categories) {
      if (!category || !metadata) {
        continue;
      }
      options.push({
        id: category.id,
        versionId: metadata.versionId,
        name: category.name,
        displayName: translate(category.name),
        chipIds: category.chipIds,
        almanac: category.almanac,
        collection: {
          id: plantProfile.id,
          name: plantProfile.name,
          isCarbon: Boolean(plantProfile.carbon),
        },
      });
    }
  }

  return options;
};

const hasAllFixedCategories = (categories: { name: string }[]): boolean => {
  const names = new Set(categories.map((c) => c.name));
  return FIXED_CATEGORY_NAMES.isSubsetOf(names);
};

const fixedCategoryByName = (name: string): FixedCategory | undefined =>
  FIXED_CATEGORIES.find(
    (category) => category.name.toLowerCase() === name.toLowerCase()
  );

export const translateCategoryName = (t: TFunction, name: string): string => {
  const i18nKey = FIXED_CATEGORIES.find(
    (category) => category.name === name
  )?.i18nKey;
  return i18nKey ? titleCase(t(i18nKey)) : name;
};

// Maps a translated (or English) fixed category name back to the English name.
// Returns the input unchanged if it doesn't match any fixed category.
export const toEnglishCategoryName = (t: TFunction, name: string): string => {
  const normalized = name.toLowerCase();
  for (const category of FIXED_CATEGORIES) {
    if (
      normalized === category.name.toLowerCase() ||
      normalized === titleCase(t(category.i18nKey)).toLowerCase()
    ) {
      return category.name;
    }
  }
  return name;
};

export const translateCategoryNames = (
  t: TFunction,
  categories: { name: string }[]
): ((name: string) => string) => {
  const shouldTranslate = hasAllFixedCategories(categories);
  return (name: string) =>
    shouldTranslate ? translateCategoryName(t, name) : name;
};

/** Fixed profiles without almanacs sort Crop-first / A–Z; otherwise keep list order. */
export const toRadioChip = (
  i18n: I18n,
  t: TFunction,
  categories: Category[],
  sortFixed: boolean
): RadioChipOption[] => {
  const displayName = translateCategoryNames(t, categories);
  const ordered = sortFixed
    ? [...categories].sort(sortCategoryOptions(i18n))
    : categories;
  return ordered.map(({ id, name }, index) => ({
    id,
    name: displayName(name),
    color: CHIP_COLORS[index % CHIP_COLORS.length],
  }));
};

// used to differentiate input values of plain text string vs a fully fledged Category object
export const isCategoryNameOnly = (value: Category | string): value is string =>
  typeof value === "string";

export const getCategoryOptionLabel = (
  t: TFunction,
  { displayName, chipIds }: CategoryOption
): string => {
  const chipInfo =
    chipIds.length > 0
      ? ` (${chipIds.length} ${t("models.images.image", {
          count: chipIds.length,
        })})`
      : "";
  return `${displayName}${chipInfo}`;
};

export const categoryHasAlmanac = (category: Category): boolean =>
  isValidAlmanac(category.almanac);

/** Fixed six-category profile that still needs almanacs before custom actions. */
export const needsFixedCategoryConversion = (profile: {
  categories: Category[];
  categoryRoles: Record<string, Role>;
}): boolean =>
  isFixedPlantProfile(profile) &&
  profile.categories.some((category) => !categoryHasAlmanac(category));

/**
 * Crop-first / A–Z ordering only for classic fixed profiles that have not been
 * converted (no category almanacs yet).
 */
export const shouldSortFixedCategoryOptions = (profile: {
  categories: Category[];
  categoryRoles: Record<string, Role>;
}): boolean =>
  isFixedPlantProfile(profile) &&
  !profile.categories.some((category) => categoryHasAlmanac(category));

export const buildDeleteCategoryChanges = (
  categoryId: string
): CategoryChange => ({
  delete: { id: categoryId },
});

export const toDraftCategory = (category: Category): Category =>
  structuredClone(category);

export const buildUpdateCategoryChange = (
  saved: SavedCategory,
  draft: Category
): CategoryChange => {
  const parentVersionId = saved.metadata?.versionId;
  if (!parentVersionId) {
    throw new Error("Saved category is missing version metadata");
  }
  if (!draft.id) {
    throw new Error("Draft category is missing id");
  }

  if (!draft.almanac) {
    throw new Error("Almanac information is required");
  }

  return {
    update: {
      metadata: {
        versionId: uuid(),
        parentVersionId,
        active: true,
        lastKnownActiveVersionId: parentVersionId,
      },
      category: {
        id: draft.id,
        name: draft.name,
        chipIds: [...draft.chipIds],
        customerId: draft.customerId,
        carbon: draft.carbon,
        almanac: almanacWithoutFineTuneFields(draft.almanac),
      },
    },
  };
};

export const buildRevertCategoryChange = (
  categoryId: string,
  parentVersionId: string,
  revertToVersionId: string
): CategoryChange => ({
  update: {
    metadata: {
      versionId: uuid(),
      parentVersionId,
      active: true,
      lastKnownActiveVersionId: parentVersionId,
    },
    revert: {
      id: categoryId,
      revertVersionId: revertToVersionId,
    },
  },
});

const findAlmanacCategoryByTypeId = (
  almanac: AlmanacConfig,
  categoryId: string
): AlmanacTypeCategory | undefined =>
  almanac.categories.find((category) => category.type?.category === categoryId);

export const getAlmanacCategoryForFixedName = (
  almanac: AlmanacConfig,
  fixedCategoryEnglishName: string
): AlmanacTypeCategory | undefined => {
  const originId = fixedCategoryByName(
    fixedCategoryEnglishName
  )?.almanacCategoryId;
  if (!originId) {
    return undefined;
  }

  return (
    findAlmanacCategoryByTypeId(almanac, originId) ??
    findAlmanacCategoryByTypeId(almanac, ALMANAC_CATEGORY_DEFAULT)
  );
};

/**
 * Reverse lookup of a stored `Almanac.originCategoryId` onto a "most similar to"
 * option. Scoped to `MOST_SIMILAR_TO_OPTIONS` so the Broadleaf UUID is Broadleaf,
 * never Disregard. Empty and unknown ids are unset.
 */
export const mostSimilarToForOriginCategoryId = (
  originCategoryId: string
): MostSimilarToOption | undefined =>
  MOST_SIMILAR_TO_OPTIONS.find(
    (name) => fixedCategoryByName(name)?.almanacCategoryId === originCategoryId
  );

export type ResolvedOriginCategory = MostSimilarToOption | "unknown";

/**
 * Stored origin category for display. Empty is unset. A non-empty id that is
 * not a most-similar-to option is unknown rather than a missing value.
 */
export const resolveOriginCategory = (
  originCategoryId: string | undefined
): ResolvedOriginCategory | undefined => {
  if (!originCategoryId) {
    return undefined;
  }
  return mostSimilarToForOriginCategoryId(originCategoryId) ?? "unknown";
};

/**
 * Carbon-default shoot-time curve for a stored origin category.
 * Unknown ids do not fall back to the default curve.
 */
export const comparisonAlmanacForOriginCategoryId = (
  carbonDefault: AlmanacConfig | undefined,
  originCategoryId: string | undefined
): Almanac | undefined => {
  if (
    !carbonDefault ||
    !originCategoryId ||
    !mostSimilarToForOriginCategoryId(originCategoryId)
  ) {
    return undefined;
  }
  const typeCategory =
    findAlmanacCategoryByTypeId(carbonDefault, originCategoryId) ??
    findAlmanacCategoryByTypeId(carbonDefault, ALMANAC_CATEGORY_DEFAULT);
  return typeCategory ? almanacFromTypeCategory(typeCategory) : undefined;
};

export type BuildCreateCategoryTemplateChangeArgs = {
  newCategoryId: string;
  versionId: string;
  name: string;
  chipIds: string[];
  almanac: Almanac;
  customerUuid?: string;
};

export const buildCreateCategoryTemplateChange = ({
  newCategoryId,
  versionId,
  name,
  chipIds,
  almanac,
  customerUuid,
}: BuildCreateCategoryTemplateChangeArgs): CategoryChange => ({
  create: {
    metadata: { versionId },
    library: true,
    category: {
      id: newCategoryId,
      name,
      chipIds: [...chipIds],
      ...(customerUuid ? { customerId: customerUuid } : {}),
      almanac: almanacWithoutFineTuneFields(almanac),
    },
  },
});
